/**
 * Admin → Builder Pricing tab.
 *
 * Single editor for every coin cost the Pal Builder applies on
 * delivery: 6 passive-tier costs, the high-level threshold + surcharge,
 * and the per-active-skill cost. Backed by `/api/admin/builder-pricing`
 * which persists into `economy_settings` (`builder_pricing.*` keys).
 *
 * Live preview at the bottom shows the cost of a worked example so
 * staff can sanity-check tier weighting without sending a real pal.
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Loader } from "@/components/Atoms";
import { Coins, RotateCcw, Save, Wand2, Zap } from "lucide-react";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";

const FIELDS = [
  { key: "tier_4_cost",          label: "Legendary (Tier 4)", icon: "🟡", min: 0,  max: 1_000_000, group: "passive" },
  { key: "tier_3_cost",          label: "Tier 3 (Rare)",      icon: "🟣", min: 0,  max: 1_000_000, group: "passive" },
  { key: "tier_2_cost",          label: "Tier 2 (Uncommon)",  icon: "🟢", min: 0,  max: 1_000_000, group: "passive" },
  { key: "tier_1_cost",          label: "Tier 1 (Common)",    icon: "🔵", min: 0,  max: 1_000_000, group: "passive" },
  { key: "tier_0_cost",          label: "Neutral (Tier 0)",   icon: "⚪", min: 0,  max: 1_000_000, group: "passive" },
  { key: "tier_neg_cost",        label: "Negative passives",  icon: "🔴", min: 0,  max: 1_000_000, group: "passive" },
  { key: "active_skill_cost",    label: "Per active skill",   icon: "⚔️", min: 0,  max: 1_000_000, group: "active",
    hint: "Charged per active skill picked (max 3 per pal)." },
  { key: "high_level_threshold", label: "High-level threshold (Lv >)", icon: "🚀", min: 1, max: 255, group: "surcharge",
    hint: "Vanilla cap = 65. Pals delivered above this level pay the surcharge below (modded servers only)." },
  { key: "high_level_surcharge", label: "High-level surcharge",        icon: "💎", min: 0, max: 1_000_000, group: "surcharge" },
  { key: "min_delivery_cost",    label: "Base delivery cost",        icon: "🛡️", min: 0, max: 1_000_000, group: "surcharge",
    hint: "Every delivery starts here — passives, active skills, level surcharge, Lucky fee and species taxes are all ADDED on top." },
  { key: "lucky_variant_cost",   label: "Lucky (shiny) variant fee",     icon: "✨", min: 0, max: 1_000_000, group: "surcharge",
    hint: "Extra fee when the Lucky toggle is on in the Builder — the pal appears as a shiny in-game." },
  { key: "world_tree_min_cost",  label: "World Tree pal tax (stacks)",        icon: "🌳", min: 0, max: 1_000_000, group: "tier_floor",
    hint: "Added on TOP of the 1.0 pal tax for World Tree endgame pals (Panthalus, Astralym, Renjishi, Aegidron…) — they're 1.0 pals too, so they pay both." },
  { key: "new_pal_min_cost",     label: "Palworld 1.0 pal tax",           icon: "🆕", min: 0, max: 1_000_000, group: "tier_floor",
    hint: "Added to every Palworld 1.0 addition (Boltmane, Bulldosu, Tetroise…) AND to every World Tree pal. A line item, not a floor." },
];

const GROUPS = [
  { id: "passive",   label: "Passive tier costs", icon: Wand2 },
  { id: "active",    label: "Active skill cost",  icon: Zap },
  { id: "surcharge", label: "Level surcharge + floor", icon: Coins },
  { id: "tier_floor", label: "Species surcharges (1.0 pals)", icon: Coins },
];


export function BuilderPricing() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [pricing, setPricing] = useState({});
  const [defaults, setDefaults] = useState({});
  const [draft, setDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/builder-pricing");
      setPricing(data.pricing || {});
      setDefaults(data.defaults || {});
      setDraft(data.pricing || {});
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => {
    return FIELDS.some((f) => Number(draft[f.key]) !== Number(pricing[f.key]));
  }, [draft, pricing]);

  const onChange = (key, value) => {
    const v = value === "" ? "" : Math.max(0, Math.floor(Number(value) || 0));
    setDraft((d) => ({ ...d, [key]: v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => {
        const v = Number(draft[f.key]);
        if (Number.isFinite(v) && v !== Number(pricing[f.key])) payload[f.key] = v;
      });
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save.");
        return;
      }
      const { data } = await api.put("/admin/builder-pricing", payload);
      setPricing(data.pricing || {});
      setDraft(data.pricing || {});
      toast.success(`Saved ${Object.keys(payload).length} pricing field${Object.keys(payload).length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const resetToDefaults = async () => {
    if (!confirm("Reset every builder-pricing value back to the original defaults?")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/builder-pricing/reset");
      setPricing(data.pricing || {});
      setDraft(data.pricing || {});
      toast.success("Pricing reset to defaults.");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setResetting(false); }
  };

  // Worked example: Lv 70 build with 1× Legendary + 1× Tier 3 + 1× Tier 1 + 2× active skills.
  const sampleCost = useMemo(() => {
    const p = draft;
    const passives = (Number(p.tier_4_cost) || 0)
                   + (Number(p.tier_3_cost) || 0)
                   + (Number(p.tier_1_cost) || 0);
    const skills = (Number(p.active_skill_cost) || 0) * 2;
    const surcharge = 70 > (Number(p.high_level_threshold) || 65) ? (Number(p.high_level_surcharge) || 0) : 0;
    return { passives, skills, surcharge, total: passives + skills + surcharge };
  }, [draft]);

  if (loading) return <Loader />;

  return (
    <div className="space-y-4 fade-up" data-testid="admin-builder-pricing">
      <div className="glass-card p-4">
        <h2 className="text-base text-[var(--bone)] mb-1 flex items-center gap-2">
          <Coins size={16} className="text-amber-300" /> Pal Builder Pricing
        </h2>
        <p className="text-xs text-[var(--muted)] leading-relaxed">
          Coin costs the Pal Builder applies on every delivery. Changes take effect on the next delivery within ~5 seconds (cache TTL). All values are integers, 0 to 1,000,000. The frontend cost preview refreshes on the next page load.
        </p>
      </div>

      {GROUPS.map((g) => {
        const groupFields = FIELDS.filter((f) => f.group === g.id);
        if (groupFields.length === 0) return null;
        const Icon = g.icon;
        return (
          <div key={g.id} className="glass-card p-4">
            <h3 className="text-sm text-[var(--bone)] mb-3 flex items-center gap-2">
              <Icon size={14} className="text-[var(--teal)]" /> {g.label}
            </h3>
            <div className="grid gap-2">
              {groupFields.map((f) => {
                const cur = draft[f.key] ?? "";
                const def = defaults[f.key];
                const isDirty = Number(cur) !== Number(pricing[f.key]);
                const isCustom = def !== undefined && Number(cur) !== Number(def);
                return (
                  <div key={f.key} className="flex items-center gap-3 px-3 py-2 rounded bg-black/20 border border-white/5">
                    <span className="text-base shrink-0" aria-hidden>{f.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[var(--bone)] font-medium">{f.label}</div>
                      {f.hint && <div className="text-[10px] text-[var(--muted)] leading-snug mt-0.5">{f.hint}</div>}
                      {def !== undefined && (
                        <div className="text-[10px] text-[var(--muted)] mt-0.5">
                          default: <span className="tabular-nums">{def.toLocaleString()}</span>
                          {isCustom && <span className="ml-2 text-amber-300">(custom)</span>}
                        </div>
                      )}
                    </div>
                    <input
                      type="number"
                      min={f.min}
                      max={f.max}
                      step="1"
                      value={cur}
                      onChange={(e) => onChange(f.key, e.target.value)}
                      className={`input w-28 sm:w-32 tabular-nums text-right ${isDirty ? "ring-1 ring-amber-400/60" : ""}`}
                      data-testid={`builder-pricing-input-${f.key}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Worked example */}
      <div className="glass-card p-4 border-teal-400/30">
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mb-2">Preview · Lv 70 build · 1× Legendary + 1× T3 + 1× T1 + 2× active skills</div>
        <div className="grid gap-1.5 text-xs">
          <div className="flex justify-between"><span className="text-[var(--muted)]">Passives</span><span className="tabular-nums">{sampleCost.passives.toLocaleString()}</span></div>
          <div className="flex justify-between"><span className="text-[var(--muted)]">Active skills (×2)</span><span className="tabular-nums">{sampleCost.skills.toLocaleString()}</span></div>
          <div className="flex justify-between"><span className="text-[var(--muted)]">Lv {Number(draft.high_level_threshold) + 5 || 70} surcharge</span><span className="tabular-nums">{sampleCost.surcharge.toLocaleString()}</span></div>
          <div className="border-t border-white/10 my-1" />
          <div className="flex justify-between text-sm" data-testid="builder-pricing-preview-total">
            <span className="text-[var(--bone)]">Total delivery cost</span>
            <span className="flex items-center gap-1.5 text-amber-300 glow-gold">
              <ChilletCoinIcon size={14} /> {sampleCost.total.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 justify-between items-center sticky bottom-2 bg-[rgba(15,12,26,0.85)] backdrop-blur p-2 -mx-2 rounded">
        <button
          onClick={resetToDefaults}
          disabled={resetting}
          className="btn btn-ghost btn-sm flex items-center gap-1.5 disabled:opacity-50"
          data-testid="builder-pricing-reset"
          title="Wipe all overrides and restore the original defaults."
        >
          <RotateCcw size={12} /> {resetting ? "Resetting…" : "Reset to defaults"}
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn btn-primary flex items-center gap-2 disabled:opacity-40"
          data-testid="builder-pricing-save"
        >
          <Save size={14} /> {saving ? "Saving…" : dirty ? "Save Changes" : "No changes"}
        </button>
      </div>
    </div>
  );
}
